/**
 * Utility functions for formatting dates in Norwegian timezone (Europe/Oslo)
 * These functions ensure dates are displayed in Norwegian time regardless of user's local timezone
 */

const NORWEGIAN_TIMEZONE = "Europe/Oslo";

/**
 * Formatter configuration for Norwegian timezone
 */
const NORWEGIAN_TIME_FORMATTER_CONFIG: Intl.DateTimeFormatOptions = {
  timeZone: NORWEGIAN_TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
};

/**
 * Helper function to parse a formatted Norwegian time string
 * Format: "MM/DD/YYYY, HH:MM:SS"
 */
const parseNorwegianTimeString = (formattedStr: string) => {
  const [datePart, timePart] = formattedStr.split(", ");
  const [month, day, year] = datePart.split("/").map(x => parseInt(x, 10));
  const [hour, minute, second] = timePart.split(":").map(x => parseInt(x, 10));
  return { year, month, day, hour, minute, second };
};

/**
 * Helper function to create a UTC Date that represents a given Norwegian local time.
 * This function tries both CET (UTC+1) and CEST (UTC+2) offsets to find the correct one.
 *
 * @param year - The year in Norwegian timezone
 * @param month - The month (1-12) in Norwegian timezone
 * @param day - The day in Norwegian timezone
 * @param hour - The hour in Norwegian timezone
 * @param minute - The minute in Norwegian timezone
 * @param second - The second in Norwegian timezone
 * @returns An object with the Date and the offset string that was used
 */
const createDateWithNorwegianOffset = (
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number
): { date: Date; offset: string } => {
  const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(hour).padStart(
    2,
    "0"
  )}:${String(minute).padStart(2, "0")}:${String(second).padStart(2, "0")}`;

  const formatter = new Intl.DateTimeFormat("en-US", NORWEGIAN_TIME_FORMATTER_CONFIG);

  // Try CET (UTC+1) first
  const cetDate = new Date(`${dateStr}+01:00`);
  const cetCheck = formatter.format(cetDate);
  const cetParsed = parseNorwegianTimeString(cetCheck);

  // If CET produces the correct Norwegian time, use it
  if (
    cetParsed.year === year &&
    cetParsed.month === month &&
    cetParsed.day === day &&
    cetParsed.hour === hour &&
    cetParsed.minute === minute &&
    cetParsed.second === second
  ) {
    return { date: cetDate, offset: "+01:00" };
  }

  // Try CEST (UTC+2)
  const cestDate = new Date(`${dateStr}+02:00`);
  const cestCheck = formatter.format(cestDate);
  const cestParsed = parseNorwegianTimeString(cestCheck);

  // If CEST produces the correct Norwegian time, use it
  if (
    cestParsed.year === year &&
    cestParsed.month === month &&
    cestParsed.day === day &&
    cestParsed.hour === hour &&
    cestParsed.minute === minute &&
    cestParsed.second === second
  ) {
    return { date: cestDate, offset: "+02:00" };
  }

  // Fallback: use CET
  return { date: cetDate, offset: "+01:00" };
};

/**
 * Formats a date string to display time in HH:mm format in Norwegian timezone
 */
export const formatTimeNorwegian = (dateString: string): string => {
  if (!dateString) return "Ikke bestemt";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("nb-NO", {
    timeZone: NORWEGIAN_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
};

/**
 * Formats a date string to display date and time in Norwegian timezone
 */
export const formatDateTimeNorwegian = (dateString: string, format: "short" | "long" = "short"): string => {
  if (!dateString) return "Ikke bestemt";
  const date = new Date(dateString);

  if (format === "long") {
    return new Intl.DateTimeFormat("nb-NO", {
      timeZone: NORWEGIAN_TIMEZONE,
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
  }

  return new Intl.DateTimeFormat("nb-NO", {
    timeZone: NORWEGIAN_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
};

/**
 * Formats a date string to display date only in Norwegian timezone
 */
export const formatDateNorwegian = (dateString: string): string => {
  if (!dateString) return "Ikke bestemt";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("nb-NO", {
    timeZone: NORWEGIAN_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

/**
 * Formats a date string to display date in DD.MM.YYYY - HH:mm format in Norwegian timezone
 */
export const formatDateWithTimeNorwegian = (dateString: string): string => {
  if (!dateString) return "Ikke bestemt";
  const date = new Date(dateString);

  // Get numeric values to avoid locale-specific formatting
  const day = parseInt(
    new Intl.DateTimeFormat("nb-NO", {
      timeZone: NORWEGIAN_TIMEZONE,
      day: "numeric",
    }).format(date),
    10
  );
  const month = parseInt(
    new Intl.DateTimeFormat("nb-NO", {
      timeZone: NORWEGIAN_TIMEZONE,
      month: "numeric",
    }).format(date),
    10
  );
  const year = parseInt(
    new Intl.DateTimeFormat("nb-NO", {
      timeZone: NORWEGIAN_TIMEZONE,
      year: "numeric",
    }).format(date),
    10
  );

  // Format with leading zeros and custom separator
  const dayStr = day.toString().padStart(2, "0");
  const monthStr = month.toString().padStart(2, "0");
  const time = formatTimeNorwegian(dateString);
  return `${dayStr}.${monthStr}.${year} - ${time}`;
};

/**
 * Gets date components (day, month, year, hours, minutes) in Norwegian timezone
 */
export const getDateComponentsNorwegian = (dateString: string) => {
  if (!dateString) return null;
  const date = new Date(dateString);

  const day = parseInt(
    new Intl.DateTimeFormat("nb-NO", {
      timeZone: NORWEGIAN_TIMEZONE,
      day: "numeric",
    }).format(date),
    10
  );

  const month =
    parseInt(
      new Intl.DateTimeFormat("nb-NO", {
        timeZone: NORWEGIAN_TIMEZONE,
        month: "numeric",
      }).format(date),
      10
    ) - 1; // JavaScript months are 0-indexed

  const year = parseInt(
    new Intl.DateTimeFormat("nb-NO", {
      timeZone: NORWEGIAN_TIMEZONE,
      year: "numeric",
    }).format(date),
    10
  );

  const hours = parseInt(
    new Intl.DateTimeFormat("nb-NO", {
      timeZone: NORWEGIAN_TIMEZONE,
      hour: "numeric",
      hour12: false,
    }).format(date),
    10
  );

  const minutes = parseInt(
    new Intl.DateTimeFormat("nb-NO", {
      timeZone: NORWEGIAN_TIMEZONE,
      minute: "numeric",
    }).format(date),
    10
  );

  return { day, month, year, hours, minutes };
};

/**
 * Gets weekday (0-6, where 0=Sunday, 1=Monday, etc.) in Norwegian timezone
 */
export const getWeekdayNorwegian = (dateString: string): number => {
  if (!dateString) return 0;
  const date = new Date(dateString);
  // Get weekday directly in Norwegian timezone using Intl.DateTimeFormat
  const weekday = new Intl.DateTimeFormat("nb-NO", {
    timeZone: NORWEGIAN_TIMEZONE,
    weekday: "long",
  }).format(date);
  // Map Norwegian weekday names to JavaScript day numbers (0=Sunday, 1=Monday, etc.)
  const weekdayMap: { [key: string]: number } = {
    søndag: 0,
    mandag: 1,
    tirsdag: 2,
    onsdag: 3,
    torsdag: 4,
    fredag: 5,
    lørdag: 6,
  };
  return weekdayMap[weekday.toLowerCase()] ?? 0;
};

/**
 * Gets the current time in Norwegian timezone as a Date object (in UTC)
 * This is useful for countdowns and time comparisons
 * The returned Date represents the UTC time that corresponds to the current Norwegian local time
 */
export const getCurrentTimeNorwegian = (): Date => {
  const now = new Date();

  // Get current time components in Norwegian timezone
  const norwegianTimeStr = new Intl.DateTimeFormat("en-US", {
    timeZone: NORWEGIAN_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(now);

  // Parse: "MM/DD/YYYY, HH:MM:SS"
  const [datePart, timePart] = norwegianTimeStr.split(", ");
  const [month, day, year] = datePart.split("/").map(x => parseInt(x, 10));
  const [hour, minute, second] = timePart.split(":").map(x => parseInt(x, 10));

  // Use helper to create a Date with the correct offset
  return createDateWithNorwegianOffset(year, month, day, hour, minute, second).date;
};

/**
 * Converts an ISO date string to a Date object representing the time in Norwegian timezone
 * This ensures the date is interpreted correctly regardless of user's local timezone
 * The returned Date represents the UTC time that corresponds to the Norwegian local time from the ISO string
 */
export const parseDateAsNorwegianTime = (dateString: string): Date => {
  if (!dateString) return getCurrentTimeNorwegian();

  const date = new Date(dateString);

  // Get what this date represents in Norwegian timezone
  const norwegianTimeStr = new Intl.DateTimeFormat("en-US", {
    timeZone: NORWEGIAN_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);

  // Parse: "MM/DD/YYYY, HH:MM:SS"
  const [datePart, timePart] = norwegianTimeStr.split(", ");
  const [month, day, year] = datePart.split("/").map(x => parseInt(x, 10));
  const [hour, minute, second] = timePart.split(":").map(x => parseInt(x, 10));

  // Use helper to create a Date with the correct offset
  return createDateWithNorwegianOffset(year, month, day, hour, minute, second).date;
};

/**
 * Converts a moment object (from DatePicker) to an ISO string in Norwegian timezone
 * This ensures that when a user sets a time in the DatePicker, it's interpreted as Norwegian time
 * The moment object represents the time the user selected in their local timezone context,
 * but we want to interpret it as if they selected that time in Norwegian timezone.
 *
 * Example: If user in Thailand selects 16:00, we want it to be 16:00 Norwegian time,
 * not 16:00 Thailand time (which would be 10:00 in Norway).
 */
export const momentToNorwegianISO = (
  momentValue: {
    year: () => number;
    month: () => number;
    date: () => number;
    hour: () => number;
    minute: () => number;
  } | null
): string | null => {
  if (!momentValue) return null;

  // Extract components from moment (these represent what the user selected)
  const year = momentValue.year();
  const month = momentValue.month() + 1; // moment months are 0-indexed, convert to 1-indexed
  const day = momentValue.date();
  const hour = momentValue.hour();
  const minute = momentValue.minute();

  // Use helper to create a Date with the correct offset
  const { offset } = createDateWithNorwegianOffset(year, month, day, hour, minute, 0);

  // Build and return the ISO string with the correct offset
  const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(hour).padStart(
    2,
    "0"
  )}:${String(minute).padStart(2, "0")}:00`;

  return `${dateStr}${offset}`;
};
